import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { DropzoneArea } from 'material-ui-dropzone'
import { firebase } from '@firebase/app'
import '@firebase/storage'
import { useSession } from '../../session/session'
import { useClientTask } from '../../session/clientTaskSession'
import { useClient } from '../../session/clientSession'
import { v4 as uuid } from 'uuid'
import { makeStyles, Grid, IconButton } from '@material-ui/core'
import QuestionHeader from './questionHeader'
import { Description, Delete, CloudDownload } from '@material-ui/icons'
import DialogOptions from '../../dialog/dialogOptions'
import { localize } from 'components/localization/strings'
// eslint-disable-next-line no-undef
const downloadjs = require('downloadjs')


const useStyles = makeStyles(() => ({
  dropzoneClass: {
    minHeight: '0px'
  },
  tinyThumbnail: {
    maxWidth: '100%',
    height: '20px'
  },
  thumbnail: {
    maxWidth: '100%',
    height: '70px'
  },
  largeThumbnail: {
    maxWidth: '100%',
    height: '200px'
  },
  thumbnailHolderLarge: {
    marginTop: '10px',
    marginBottom: '10px',
    padding: '5px',
    border: '1px solid var(--main-background)',
  },
  thumbnailHolder: {
    position: 'relative',
    textAlign: 'center',
    '& button': {
      visibility: 'hidden'
    },
    '&:hover': {
      border: '1px solid var(--button-color)',
      '& button': {
        visibility: 'visible'
      }
    }
  },
}))

export function FileThumbnail({ fileURI, filename, del, allowModify, allowDownload, size }) {
  const classes = useStyles()
  const session = useSession()
  const [open, setOpen] = useState(false)

  const download = () => {
    const xhr = new XMLHttpRequest()
    xhr.responseType = 'blob'
    xhr.onprogress = event => {
      const progress = Math.round((event.loaded / (event.total || 1)) * 100)
      session.snackbar.setSnackbar({ duration: null, message: `Downloading ${filename} - ${progress}%` })
    }
    xhr.onload = () => {
      downloadjs(xhr.response, filename)
      session.snackbar.setSnackbar({ message: 'Download Complete' })
    }
    xhr.open('GET', fileURI)
    xhr.send()
  }

  let thumbnailClass = classes.thumbnail
  if (size === 'large') thumbnailClass = classes.largeThumbnail
  else if (size === 'tiny') thumbnailClass = classes.tinyThumbnail


  let fileContentHolder
  if (filename.match(/.(jpg|jpeg|png|gif|webp|svg)$/i)) {
    fileContentHolder = (<img src={fileURI} className={thumbnailClass} />)
  } else if (filename.match(/.(mp4|ogg|webm)$/i)) {
    fileContentHolder = (<video controls src={fileURI} className={thumbnailClass} />)
  } else if (filename.match(/.(mp3|wav)$/i)) {
    fileContentHolder = (<audio controls src={fileURI} className={thumbnailClass} />)
  } else if (filename.match(/.(pdf)$/i)) {
    fileContentHolder = (<embed src={fileURI} type="application/pdf" className={thumbnailClass} />)
  } else {
    fileContentHolder = (<Description style={{ fontSize: 40 }} className={thumbnailClass} />)
  }
  return (
    <div className={`${classes.thumbnailHolder} ${size === 'large' ? classes.thumbnailHolderLarge : ''}`}>
      {allowModify && allowDownload &&
        <div className={classes.buttons}>
          {allowDownload && <IconButton onClick={download} ><CloudDownload /></IconButton>}
          {allowModify && <IconButton onClick={() => setOpen(true)}><Delete /></IconButton>}
        </div>
      }
      <div style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {fileContentHolder}
      </div>
      {size != 'tiny' ? filename : ''}
      <DialogOptions content='Are you sure you want to delete this file?' open={open} setOpen={setOpen} options={[{ content: 'Delete File', action: del }]} />
    </div>
  )
}

FileThumbnail.propTypes = {
  fileURI: PropTypes.string.isRequired,
  filename: PropTypes.string.isRequired,
  del: PropTypes.func,
  allowModify: PropTypes.bool,
  allowDownload: PropTypes.bool,
  size: PropTypes.string
}

export default function RenderUpload({ elmIndex, elm, valIndex, valueChange, allowModify }) {
  const classes = useStyles()
  const session = useSession()
  const clientSession = useClient()
  const clientTaskSession = useClientTask()
  if (!elm.Value[valIndex]) elm.Value[valIndex] = ['']
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [hasCredentials, setHasCredentials] = useState(true)

  const filepath = clientSession?.clientRef?.id && clientTaskSession?.taskRef?.id ? `${session.org.ref.id}/${clientSession.clientRef.id}/${clientTaskSession.sectionIndex}/${clientTaskSession.taskRef.id}/${elmIndex}` : ''

  useEffect(() => setHasCredentials(!(session.external && session.user.public && session.user.uid != clientSession.clientRef?.id)), [clientSession.clientRef?.id, session.external, session.user.public, session.user.uid])

  useEffect(() => {
    const renderFiles = async () => {
      const files = await Promise.all(elm.Value[valIndex].map(async fullpath => {
        if (!fullpath) return null
        if (fullpath?.Download) return [fullpath?.Download, fullpath?.FileRef.split('/').pop(), fullpath?.FileRef]
        const filename = fullpath.split('/').pop()
        const fileExists = (await firebase.storage().ref(fullpath.substr(0, fullpath.lastIndexOf('/'))).list()).items.filter(e => e.name === filename)
        if (fileExists.length > 0) {
          const fileRef = firebase.storage().ref(fullpath)
          let url
          try { url = await fileRef.getDownloadURL() } catch (err) { console.error(err) }
          return [url, filename, fullpath]
        } else {
          return null
        }
      }))
      setUploadedFiles(files.filter(e => e))
    }
    renderFiles()
  }, [elm, valIndex])

  const uploadFiles = async (files) => {
    const rand = uuid()
    for (let file of files) {
      const fileRef = firebase.storage().ref(`${filepath}/${rand}/${file.name}`)
      try {
        const uploadTask = fileRef.put(file)
        uploadTask.on('state_changed', snap => {
          const progress = Math.round((snap.bytesTransferred / (snap.totalBytes || 1)) * 100)
          session.snackbar.setSnackbar({ duration: null, message: `Uploading ${file.name} - ${progress}%` })
        })
        await uploadTask
        session.snackbar.setSnackbar({ message: 'Upload Complete' })
        const newFiles = uploadedFiles.concat([[await fileRef.getDownloadURL(), file.name, `${filepath}/${rand}/${file.name}`]])
        setUploadedFiles(newFiles)

        // Update the saved
        valueChange(elmIndex, valIndex, newFiles.map(e => { return { FileRef: e[2], Download: e[0] } }))
      } catch (e) {
        console.error(e)
        session.snackbar.setSnackbar({ message: 'File Upload Permission Error. Please sign out and try again.', severity: 'error' })
      }
    }
  }

  return <div key={elmIndex}>
    <QuestionHeader helpTooltip={elm.ShowHelpTooltip ? elm.HelpTooltip : ''} prompt={`${localize('Attach Files', clientSession?.client?.Language)}: ${elm.Prompt ?? ''}`} allowBlanks={!!elm.AllowBlanks} />
    {(!session.external || (hasCredentials && allowModify && filepath)) && <DropzoneArea
      onChange={!filepath ? () => { } : uploadFiles}
      dropzoneText=''
      showPreviews={false}
      showPreviewsInDropzone={false}
      acceptedFiles={['']}
      dropzoneClass={classes.dropzoneClass}
      showAlerts={false}
      filesLimit={1}
      maxFileSize={1e+10} // 10 GB Max file size
    />}
    <Grid container item spacing={2} xs={12}>
      {!hasCredentials && <Grid item xs={12}>File Upload Credentials Changed: &nbsp;
        <a href="#" onClick={() => {
          if (confirm('Unsaved Changes Will Be Lost. Please make sure your changes are saved.'))
            session.signout(location.href)
        }}>Click here to Sign Out and Refresh Credentials</a>
      </Grid>}
      {uploadedFiles.map(f =>
        <Grid item xs={12} md={3} key={f[0]}>
          <FileThumbnail fileURI={f[0]} filename={f[1]} allowModify={allowModify} allowDownload={true} del={async () => {
            const fileRef = firebase.storage().ref(f[2])
            try { await fileRef.delete() } catch (e) { console.log('File not found') }
            const newFiles = uploadedFiles.filter(e => e[2] != f[2])
            setUploadedFiles(newFiles)
            // Update the saved 
            valueChange(elmIndex, valIndex, newFiles.map(e => e[2]))
          }} />
        </Grid>)}
    </Grid>
  </div>
}

RenderUpload.propTypes = {
  elmIndex: PropTypes.number.isRequired,
  elm: PropTypes.object.isRequired,
  valIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  valueChange: PropTypes.func.isRequired,
  allowModify: PropTypes.bool.isRequired,
}